import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { evaluatePasswordComplexity } from '../../../../utils/passwordPolicy'
import { changePasswordRequest } from '../api/passwordRecoveryApi'

export function usePasswordResetScreen() {
  const navigate = useNavigate()
  const location = useLocation()

  const tenantUsername = (
    location.state?.tenantUsername ||
    localStorage.getItem('rememberTenantUsername') ||
    ''
  ).trim().toUpperCase()
  const email = (location.state?.email || '').trim().toLowerCase()
  const mode = location.state?.mode || 'forgot-password'
  const verifiedOtp = location.state?.otp || null
  const currentPassword = location.state?.currentPassword || ''
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const complexity = useMemo(
    () => evaluatePasswordComplexity(newPassword),
    [newPassword]
  )

  const isComplexEnough = complexity.isMet

  useEffect(() => {
    if (!email) {
      navigate('/login', { replace: true })
    }
  }, [email, navigate])

  useEffect(() => {
    if (mode === 'forgot-password' && !verifiedOtp) {
      navigate('/auth/otp-verification', { replace: true, state: { tenantUsername, email } })
    }

    if (mode === 'first-login' && !currentPassword) {
      navigate('/login', { replace: true })
    }
  }, [currentPassword, email, mode, navigate, tenantUsername, verifiedOtp])

  async function handlePasswordReset(event) {
    event.preventDefault()
    setError('')
    setSuccessMessage('')

    if (!tenantUsername) {
      setError('Tenant Username is missing. Please start again from login.')
      return
    }

    if (!email) {
      setError('Recovery session expired. Start again from forgot password.')
      return
    }

    if (!newPassword.trim()) {
      setError('Enter your new password.')
      return
    }

    if (!isComplexEnough) {
      setError('Password does not meet complexity standards.')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation must match.')
      return
    }

    if (mode === 'first-login' && !currentPassword.trim()) {
      setError('Current password is required for this reset flow.')
      return
    }

    setIsSubmitting(true)
    try {
      await changePasswordRequest({
        tenantUsername,
        email,
        newPassword,
        otp: mode === 'forgot-password' ? verifiedOtp : null,
        currentPassword: mode === 'first-login' ? currentPassword : null,
      })

      setSuccessMessage('Password updated successfully. Redirecting to login...')
      navigate('/login', {
        replace: true,
        state: {
          tenantUsername,
          email,
          message: 'Password updated successfully. Please login with your new password.',
        },
      })
    } catch (requestError) {
      setError(
        extractApiErrorMessage(
          requestError,
          'Unable to update password right now. Please try again.'
        )
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleBackToOtp() {
    if (mode === 'first-login') {
      navigate('/login', { replace: true, state: { tenantUsername, email } })
      return
    }

    navigate('/auth/otp-verification', { replace: true, state: { tenantUsername, email } })
  }

  return {
    email,
    mode,
    verifiedOtp,
    currentPassword,
    newPassword,
    confirmPassword,
    complexity,
    isSubmitting,
    error,
    successMessage,
    setNewPassword,
    setConfirmPassword,
    handlePasswordReset,
    handleBackToOtp,
  }
}
